import React from "react";
import queryString from "query-string-for-all";
import { DataGridCell, Main } from "@conserveit/blocks";
import { Direction } from "../../../utils/columns";

import SiteGroupHeading from "../ExpandableGroup/components/SiteGroupHeading";
import SiteGroupHeader from "../ExpandableGroup/components/SiteGroupHeader";
import SiteGroupRow from "../ExpandableGroup/components/SiteGroupRow";
import RuleGroupHeading from "../ExpandableGroup/components/RuleGroupHeading";
import RuleGroupHeader from "../ExpandableGroup/components/RuleGroupHeader";
import RuleGroupRow from "../ExpandableGroup/components/RuleGroupRow";
import PriorityGroupHeading from "../ExpandableGroup/components/PriorityGroupHeading";
import PriorityGroupHeader from "../ExpandableGroup/components/PriorityGroupHeader";
import PriorityGroupRow from "../ExpandableGroup/components/PriorityGroupRow";
import GroupFooter from "../ExpandableGroup/components/GroupFooter";
import { GroupBy, SortBy, Category } from "./types";
import { RouteComponentProps } from "react-router";
import { ActionedCell, ActionedMain } from "../ExpandableGroup/GroupRow.styles";
import { history } from "../../../browserHistory";
import { SEARCH_KEY } from "./constants";
import { CsvObject } from "../../Exporter/utils";

import { groupByRule, groupBySite, groupByPriority } from "./groups";
import { sortByHits, sortByEquips, sortByInsights, sortByAlpha } from "./sorts";
import { ViewMode } from "../../../context/analysis/types";

export interface GroupHeadingProps {
  instance: Insite.Insights.ComposedInsight;
  insightsCount: number;
  insightsCountToday: number;
  equipsCount: number;
  unassociatedEquipsCount: number;
}

export interface GroupHeaderProps {
  activeSort: string;
  hitCount: number;
  equipCount: number;
  groupCount: number;
  sortDirection: Direction;
  onSort(sort: string): void;
}

export interface GroupFooterProps {
  moreCount: number;
  currentCount: number;
  totalCount: number;
  onLoadMore(): void;
  onLoadAll(): void;
  exportData?: Insite.Insights.ComposedInsight[];
  viewMode?: ViewMode;
}

export interface GroupAssets {
  Heading: React.ElementType<GroupHeadingProps>;
  Header: React.ElementType<GroupHeaderProps>;
  Row: React.ElementType<
    Insite.Insights.ComposedInsight & { showSnoozed: boolean }
  >;
  Footer: React.ElementType<GroupFooterProps>;
  grid: {
    columns?: number;
    group?: Array<{
      start: number;
      end: number;
    }>;
    grow?: Array<number>;
    hide?: Array<number>;
  };
}

export function getGroupAssets(group: GroupBy): GroupAssets {
  switch (group) {
    case GroupBy.Rule:
      return {
        Heading: RuleGroupHeading,
        Header: RuleGroupHeader,
        Row: RuleGroupRow,
        Footer: GroupFooter,
        grid: {
          columns: 5,
          group: [
            { start: 2, end: 3 },
            { start: 4, end: 5 },
          ],
          grow: [2],
          hide: [],
        },
      };
    case GroupBy.Priority:
      return {
        Heading: PriorityGroupHeading,
        Header: PriorityGroupHeader,
        Row: PriorityGroupRow,
        Footer: GroupFooter,
        grid: {
          columns: 9,
          group: [
            { start: 3, end: 4 },
            { start: 6, end: 7 },
            { start: 8, end: 9 },
          ],
          grow: [3, 6],
          hide: [2],
        },
      };
    case GroupBy.Site:
    default:
      return {
        Heading: SiteGroupHeading,
        Header: SiteGroupHeader,
        Row: SiteGroupRow,
        Footer: GroupFooter,
        grid: {
          columns: 7,
          group: [
            { start: 4, end: 5 },
            { start: 6, end: 7 },
          ],
          grow: [4],
          hide: [],
        },
      };
  }
}

export function filterByCategory(
  category: Category,
  group: GroupBy,
  data: Insite.AppState["monitoring"]
) {
  return Object.values(data[group.toLowerCase()])
    .map((hitIds) => {
      const filteredHits = hitIds
        .filter(
          (id: number) =>
            // TODO: Inefficient, pls optimise
            category === Category.All ||
            (data["category"][category] &&
              data["category"][category].includes(id) &&
              data.hit[id])
        )
        .map((id: number) => data["hit"][id])
        .filter((occ: any) => occ !== undefined);

      return filteredHits;
    })
    .filter((arr) => arr.length > 0);
}

export function sortFilteredData(
  data: Insite.Insights.ComposedInsight[][],
  groupBy: GroupBy,
  sortBy: SortBy
) {
  switch (sortBy) {
    case SortBy.Equips:
      return sortByEquips(data);
    case SortBy.InsightsAsc:
      return sortByInsights(data, "asc");
    case SortBy.InsightsDesc:
      return sortByInsights(data, "desc");
    case SortBy.AlphaAsc:
      return sortByAlpha(data, groupBy, "asc");
    case SortBy.AlphaDesc:
      return sortByAlpha(data, groupBy, "desc");
    case SortBy.Hits:
    default:
      return sortByHits(data);
  }
}

export function groupByGroupName(
  group: GroupBy,
  occurrences: Insite.Insights.ComposedOccurrence[] = []
) {
  switch (group) {
    case GroupBy.Rule:
      return groupByRule(occurrences);
    case GroupBy.Priority:
      return groupByPriority(occurrences, false);
    case GroupBy.Site:
    default:
      return groupBySite(occurrences, false);
  }
}

export function getHitsCount(
  insights: Insite.Insights.ComposedInsight[] = [],
  showSnoozed?: boolean
) {
  const propAccessor = showSnoozed ? "hits" : "activeHits";
  return insights.reduce((count, insight) => count + insight[propAccessor], 0);
}

export function getEquipsCount(
  insights: Insite.Insights.ComposedInsight[] = [],
  showSnoozed?: boolean
) {
  const propAccessor = showSnoozed ? "targetCount" : "activeTargetCount";
  return insights.reduce((count, insight) => count + insight[propAccessor], 0);
}

export function getUnassociatedEquipsCount(
  insights: Insite.Insights.ComposedInsight[] = [],
  showSnoozed?: boolean
) {
  const targetAccessor = showSnoozed ? "targetCount" : "activeTargetCount";
  const actionAccessor = showSnoozed ? "actionsCount" : "activeActionsCount";
  return insights.reduce(
    (count, insight) =>
      count + (insight[targetAccessor] - insight[actionAccessor]),
    0
  );
}

export function getInsightsCount(
  insightGroups: Insite.Insights.ComposedInsight[][] = [],
  showSnoozed?: boolean
) {
  return showSnoozed
    ? insightGroups.reduce((count, insights) => count + insights.length, 0)
    : insightGroups.reduce((count, insights) => {
        return (
          count + insights.filter((insight) => !insight.isAllSnoozed).length
        );
      }, 0);
}

export function getSnoozedCount(
  insightGroups: Insite.Insights.ComposedInsight[][] = []
) {
  return insightGroups.reduce((totalSnoozed, insightGroup) => {
    const insightGroupSnoozedCount = insightGroup.reduce(
      (snoozedCount, insight) =>
        snoozedCount + (insight.targetCount - insight.activeTargetCount),
      0
    );
    return totalSnoozed + insightGroupSnoozedCount;
  }, 0);
}
export function getMonitoringCounts(
  group: GroupBy,
  grouped: Insite.Insights.ComposedOccurrence[][],
  showSnoozed: boolean
) {
  const siteCount = grouped.reduce((sites: string[], occurrences) => {
    return sites.concat(occurrences.map((occurrence) => occurrence.siteId));
  }, []);
  const count = new Set(siteCount);

  return {
    siteCount: count.size,
    equipCount: grouped.reduce(
      (count, occurrences) =>
        count +
        getEquipsCount(groupByGroupName(group, occurrences), showSnoozed),
      0
    ),
    analysisCount: grouped.reduce(
      (count, occurrences) =>
        count + getHitsCount(groupByGroupName(group, occurrences), showSnoozed),
      0
    ),
  };
}

export function getGroupId(
  group: GroupBy,
  instance: Insite.Insights.ComposedInsight
): string {
  switch (group) {
    case GroupBy.Priority:
      return `${group}-${instance.priority}`;
    case GroupBy.Rule:
      return `${group}-${instance.ruleId}`;
    default:
      return `${group}-${instance.site.syncoId}`;
  }
}

export function updateQueryString(
  query: string,
  history: RouteComponentProps["history"],
  queryKey: string
) {
  const qs = queryString.parse(history.location.search);

  const val = query.length > 0 ? query : undefined;
  const stringified = queryString.stringify({ ...qs, [queryKey]: val });
  history.replace({
    search: stringified,
  });
}

export type SplitActioned<T> = {
  actioned: T[];
  unactioned: T[];
};
export function splitActioned(insights: Insite.Insights.ComposedInsight[]) {
  return insights.reduce<SplitActioned<Insite.Insights.ComposedInsight>>(
    (split, insight) => {
      if (insight.targets.length === insight.actionsCount) {
        split.actioned.push(insight);
      } else {
        split.unactioned.push(insight);
      }
      return split;
    },
    { actioned: [], unactioned: [] }
  );
}

/**
 * Return different DataGridCell and Main style and wording on equips count based on whether actionsCount equals to targetCount
 * @param insight
 * @param showSnoozed
 */
export function getActionsAssociatedState(
  insight: Insite.Insights.ComposedInsight,
  showSnoozed: boolean
) {
  const { targetCount, activeTargetCount, actionsCount } = insight;
  const displayTargetCount = showSnoozed ? targetCount : activeTargetCount;

  return targetCount === actionsCount
    ? {
        Cell: ActionedCell,
        MainText: ActionedMain,
        allAssociated: true,
        displayTargetCount,
      }
    : {
        Cell: DataGridCell,
        MainText: Main,
        allAssociated: false,
        displayTargetCount,
      };
}

export function getInstantSearchQueryFromUrl() {
  const qs = React.useMemo(
    () => queryString.parse(history.location.search)[SEARCH_KEY],
    [history.location.search]
  );
  return React.useMemo(() => (typeof qs === "string" ? qs : undefined), []);
}

/** Return the category in the query string, defaults to "connectivity" if not available */
export function getCategoryFromQueryString(search: string) {
  const qs = queryString.parse(search);
  // @ts-ignore
  if (qs.category && Object.values(Category).includes(qs.category)) {
    return qs.category as Category;
  }

  return Category.All;
}

/**
 * Transform all insights gruops into a format that can be used
 * for the function getCsvString()
 * @param composed
 */
export function getAllInsightsExportData(
  composed: Insite.Insights.ComposedInsight[][]
): CsvObject {
  return composed.reduce((acc: CsvObject, items) => {
    const transformedItems = items.reduce((targetAcc: CsvObject, target) => {
      const transformed = target.targets.map((t) => ({
        siteId: t.siteId,
        siteName: t.site.label,
        state: t.site.state,
        equipName: t.targetName,
        description: t.description,
        ruleName: t.ruleName,
        ruleCategory: t.ruleCategory ? t.ruleCategory : "",
        isSnoozed: t.isSnoozed.toString(),
        targetName: t.targetName,
        reportURL: t.reportURL,
        occurredOn: t.occurredOn,
        priority: t.priority !== undefined ? t.priority : "unprioritised",
        duration: t.duration,
      }));
      return targetAcc.concat(transformed);
    }, []);

    return acc.concat(transformedItems);
  }, []);
}

/**
 * Transform single insight group for function getCsvString() to export
 * @param composed
 */
export function getGroupedInsightsExportData(
  composed: Insite.Insights.ComposedInsight[]
): CsvObject {
  return composed.reduce((acc: CsvObject, item) => {
    const transformed = item.targets.map((t) => ({
      siteId: t.siteId,
      siteName: t.site.label,
      state: t.site.state,
      equipName: t.targetName,
      description: t.description,
      ruleName: t.ruleName,
      ruleCategory: t.ruleCategory ? t.ruleCategory : "",
      isSnoozed: t.isSnoozed.toString(),
      targetName: t.targetName,
      reportURL: t.reportURL,
      occurredOn: t.occurredOn,
      priority: t.priority !== undefined ? t.priority : "unprioritised",
      duration: t.duration,
    }));
    return [...acc, ...transformed];
  }, []);
}
